/**
 * Philips Hue: Verbindungsstatus, Dock-Auswahl mit Live-Zustand und optimistische
 * Schaltbefehle (Szene, Raum-Helligkeit/Aus, einzelne Lampen).
 */
import type { HueCatalogDto, HueGroupDto, HueLightDto, HueSceneDto, HueStatusDto } from "@piano/shared";
import { ApiError } from "../api/client.js";
import { api } from "../api/index.js";
import { ui } from "./ui.svelte.js";

const EMPTY: HueCatalogDto = { groups: [], scenes: [], lights: [] };
/** Höchstens ein Helligkeitsbefehl je Gruppe/Lampe in diesem Abstand (plus Endwert). */
export const DIM_THROTTLE_MS = 800;
/** Nach einem Schaltbefehl: so lange warten, bis die Bridge den neuen Zustand hat, dann abgleichen. */
export const SETTLE_MS = 1200;

class Hue {
  status = $state<HueStatusDto | null>(null);
  /** Außerhalb des Heimnetzes: Hue ist dort serverseitig gesperrt. */
  away = $state(false);
  dock = $state<HueCatalogDto>(EMPTY);
  /** Lichtschein im Viewer nach einer Szenenwahl (Farben + Zeitpunkt). */
  echo = $state<{ colors: string[]; at: number } | null>(null);

  connected = $derived(this.status?.connected === true);
  hasDock = $derived(this.connected && this.dock.scenes.length + this.dock.lights.length > 0);
  /** Aktive angeheftete Szene (für den Farbring am Dock-Knopf). */
  activeScene = $derived(this.dock.scenes.find((s) => s.active) ?? null);

  private dimTimers = new Map<
    string,
    { timer: ReturnType<typeof setTimeout>; pending: (() => void) | null }
  >();
  private dockSeq = 0;
  private settleTimer: ReturnType<typeof setTimeout> | undefined;

  async loadStatus(): Promise<void> {
    try {
      this.status = await api.hueStatus();
      this.away = false;
    } catch (error) {
      // ohne Hue-Status bleibt alles wie bisher; unterwegs gibt es bewusst kein Licht
      this.away = error instanceof ApiError && error.code === "HUE_LAN_ONLY";
      if (this.away) this.status = null;
    }
    if (!this.connected) this.dock = EMPTY;
  }

  /**
   * Lädt die angehefteten Elemente mit Live-Zustand; still bei Fehlern (Bridge evtl. offline).
   * `fresh`: echten Zustand der Bridge lesen (Stück öffnen, Licht-Panel öffnen, nach Befehlen).
   */
  async loadDock(options: { fresh?: boolean } = {}): Promise<void> {
    if (this.status === null) await this.loadStatus(); // z. B. Stück direkt per Link geöffnet
    if (!this.connected) return;
    const seq = ++this.dockSeq;
    try {
      const dock = await api.hueDock(options.fresh === true);
      if (seq === this.dockSeq && Array.isArray(dock?.scenes)) this.dock = dock;
    } catch {
      // Panel zeigt dann den letzten Stand
    }
  }

  /** Nach Live-Ereignis „hue“ (Bridge oder anderes Gerät hat geschaltet). */
  async refresh(): Promise<void> {
    await this.loadStatus();
    await this.loadDock();
  }

  reset(): void {
    this.status = null;
    this.dock = EMPTY;
    this.echo = null;
  }

  // --- Schalten (optimistisch) -------------------------------------------------

  async recall(scene: HueSceneDto): Promise<boolean> {
    const before = this.dock;
    this.dock = {
      ...before,
      scenes: before.scenes.map((s) =>
        s.groupId === scene.groupId ? { ...s, active: s.id === scene.id } : s,
      ),
      groups: before.groups.map((g) => (g.id === scene.groupId ? { ...g, on: true } : g)),
    };
    this.echo = { colors: scene.colors, at: Date.now() };
    try {
      await api.hueRecall(scene.id);
      this.settle();
      return true;
    } catch (error) {
      this.dock = before;
      this.echo = null;
      ui.error(error, "Bridge nicht erreichbar");
      return false;
    }
  }

  async setGroupOn(group: HueGroupDto, on: boolean): Promise<void> {
    const before = this.dock;
    this.patchGroup(group.id, { on });
    // Der Raum schaltet alle seine Lampen; ausgeschaltet ist keine Szene mehr aktiv.
    this.dock = {
      ...this.dock,
      lights: this.dock.lights.map((l) => (l.roomId === group.id ? { ...l, on } : l)),
      scenes: on
        ? this.dock.scenes
        : this.dock.scenes.map((s) => (s.groupId === group.id ? { ...s, active: false } : s)),
    };
    try {
      await api.hueGroup(group.id, { on });
      this.settle();
    } catch (error) {
      this.dock = before;
      ui.error(error, "Bridge nicht erreichbar");
    }
  }

  setGroupBrightness(group: HueGroupDto, brightness: number): void {
    this.patchGroup(group.id, { on: true, brightness });
    this.throttle(`g:${group.id}`, () => api.hueGroup(group.id, { brightness }));
  }

  async setLightOn(light: HueLightDto, on: boolean): Promise<void> {
    const before = this.dock;
    this.patchLight(light.id, { on });
    // Eine Lampe an → ihr Raum ist an. (Aus: ob noch andere brennen, klärt der Abgleich danach.)
    if (on && light.roomId) this.patchGroup(light.roomId, { on: true });
    try {
      await api.hueLight(light.id, { on });
      this.settle();
    } catch (error) {
      this.dock = before;
      ui.error(error, "Bridge nicht erreichbar");
    }
  }

  setLightBrightness(light: HueLightDto, brightness: number): void {
    this.patchLight(light.id, { on: true, brightness });
    this.throttle(`l:${light.id}`, () => api.hueLight(light.id, { brightness }));
  }

  // --- intern ---------------------------------------------------------------------

  /** Kurz nach dem letzten Befehl den echten Zustand holen (Szene, Raum, Lampen gemeinsam). */
  private settle(): void {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => void this.loadDock({ fresh: true }), SETTLE_MS);
  }

  private patchGroup(id: string, patch: Partial<HueGroupDto>): void {
    this.dock = { ...this.dock, groups: this.dock.groups.map((g) => (g.id === id ? { ...g, ...patch } : g)) };
  }

  private patchLight(id: string, patch: Partial<HueLightDto>): void {
    this.dock = { ...this.dock, lights: this.dock.lights.map((l) => (l.id === id ? { ...l, ...patch } : l)) };
  }

  /** Sofort senden, danach höchstens alle DIM_THROTTLE_MS – der letzte Wert kommt immer an. */
  private throttle(key: string, send: () => Promise<void>): void {
    const run = () =>
      void send()
        .then(() => this.settle())
        .catch((error: unknown) => ui.error(error, "Bridge nicht erreichbar"));
    const slot = this.dimTimers.get(key);
    if (slot) {
      slot.pending = run;
      return;
    }
    run();
    const tick = () => {
      const current = this.dimTimers.get(key);
      if (current?.pending) {
        current.pending();
        current.pending = null;
        current.timer = setTimeout(tick, DIM_THROTTLE_MS);
      } else {
        this.dimTimers.delete(key);
      }
    };
    this.dimTimers.set(key, { timer: setTimeout(tick, DIM_THROTTLE_MS), pending: null });
  }
}

export const hue = new Hue();
